import { useCallback, useEffect, useRef, useState } from 'react';
import { reviewService } from '@/services';
import type {
  ConfirmBatchResult,
  QuotaExceededError,
} from '@/services/review.service';
import { toast, useToastStore } from '@/stores/toastStore';
import { REVIEW_COPY } from './reviewCopy';

/** Delays before the 3 automatic retries of one Confirm all. */
export const CONFIRM_RETRY_DELAYS_MS = [1_000, 3_000, 9_000] as const;

/** How long the "Tap Confirm all to try again" toast stays (YAR-555 AC13). */
export const CONFIRM_FAIL_TOAST_MS = 6_000;

export interface UseConfirmAllResult {
  /**
   * Confirm all: a new series with a NEW idempotency key and up to 3
   * automatic retries — or, after a series ran out of retries, that same
   * series again (same key), so a request that did commit replays its batch.
   */
  confirm(): Promise<void>;
  /** A confirm (or one of its retries) is in flight or waiting. */
  busy: boolean;
}

/**
 * A 4xx other than STALE_ITEMS / QUOTA_EXCEEDED (bad payload, not
 * authorized) answers the same every time: no point retrying. Network
 * failures, relay errors, 429 (rate limited — the next window answers) and
 * 5xx are worth another go.
 */
function isRetryable(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('context' in err)) {
    return true;
  }
  const ctx: unknown = err.context;
  if (typeof ctx !== 'object' || ctx === null || !('status' in ctx)) {
    return true;
  }
  const status = ctx.status;
  if (status === 429) return true;
  return !(typeof status === 'number' && status >= 400 && status < 500);
}

/** What one attempt series sends, fixed when the series starts. */
interface ConfirmSeries {
  key: string;
  householdId: string;
  /** The cards the parent saw when they tapped Confirm all. */
  itemIds: readonly string[];
  /** The members picked on those cards then (Step 2), if any. */
  itemMemberIds?: Record<string, string[]>;
}

/** A new series: a new key and a copy of what the parent sees now. */
function newSeries(opts: {
  householdId: string;
  itemIds: readonly string[];
  itemMemberIds?: Record<string, string[]>;
}): ConfirmSeries {
  const { householdId, itemIds, itemMemberIds } = opts;
  return {
    key: crypto.randomUUID(),
    householdId,
    // A copy: the list the parent saw, whatever the page renders next.
    itemIds: [...itemIds],
    itemMemberIds: itemMemberIds
      ? Object.fromEntries(
          Object.entries(itemMemberIds).map(([id, members]) => [
            id,
            [...members],
          ]),
        )
      : undefined,
  };
}

/**
 * Review v0 Confirm all (YAR-555): writes the household's whole review to
 * the schedule through `review-confirm`.
 *
 * Idempotency: one key per attempt SERIES. The automatic retries (after 1,
 * 3 and 9 s) and Try again reuse it — a request that timed out but did
 * commit is answered with the same batch, never a second one. Try again is
 * Confirm all itself (YAR-555 AC13): after the retries run out the toast
 * says so for 6 s, the bar re-enables, and the next tap sends the kept
 * series. Every Confirm all after a series ENDED (success, stale, quota,
 * a refusal — or an Undo after a success) gets a new key: an old key
 * would replay the old success without writing.
 *
 * What the parent saw (A9): the series sends `itemIds`, `householdId` and
 * `itemMemberIds` as they were when Confirm all was first tapped, on every
 * retry and on Try again. An item a refresh brings in meanwhile is never
 * confirmed unseen; it waits for the next new series.
 *
 * STALE_ITEMS and QUOTA_EXCEEDED never retry; they hand back to the page.
 */
export function useConfirmAll(opts: {
  householdId: string;
  /** Ids of every card on the list now, folded tasks included. */
  itemIds: readonly string[];
  itemMemberIds?: Record<string, string[]>;
  onConfirmed(result: ConfirmBatchResult): void;
  onStale(): void;
  onQuota(err: QuotaExceededError): void;
}): UseConfirmAllResult {
  const [busy, setBusy] = useState(false);
  // Callbacks change every render; a series in flight reads the latest.
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  });
  /** The current series; kept after a failed series for the next Confirm all. */
  const seriesRef = useRef<ConfirmSeries | null>(null);
  const runningRef = useRef(false);
  const toastIdRef = useRef<string | null>(null);
  const aliveRef = useRef(true);
  const waitRef = useRef<{
    timer: ReturnType<typeof setTimeout>;
    resolve(): void;
  } | null>(null);

  const closeToast = useCallback(() => {
    const id = toastIdRef.current;
    toastIdRef.current = null;
    if (id) useToastStore.getState().removeToast(id);
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      // Leaving the page stops the series; nothing would retry behind a
      // toast that says it is trying.
      aliveRef.current = false;
      const wait = waitRef.current;
      waitRef.current = null;
      if (wait) {
        clearTimeout(wait.timer);
        wait.resolve();
      }
      closeToast();
    };
  }, [closeToast]);

  const wait = (ms: number) =>
    new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        waitRef.current = null;
        resolve();
      }, ms);
      waitRef.current = { timer, resolve };
    });

  const runSeries = useCallback(
    async (series: ConfirmSeries) => {
      if (runningRef.current) return;
      runningRef.current = true;
      setBusy(true);
      try {
        for (let attempt = 0; ; attempt += 1) {
          const { itemMemberIds } = series;
          try {
            const result = await reviewService.confirmReviewBatch(
              series.householdId,
              series.key,
              itemMemberIds
                ? {
                    itemIds: series.itemIds,
                    item_member_ids: itemMemberIds,
                  }
                : { itemIds: series.itemIds },
            );
            if (!aliveRef.current) return;
            closeToast();
            seriesRef.current = null;
            optsRef.current.onConfirmed(result);
            return;
          } catch (err) {
            if (!aliveRef.current) return;
            if (err instanceof reviewService.StaleReviewListError) {
              // Nothing was written; the page refetches the current list.
              closeToast();
              seriesRef.current = null;
              optsRef.current.onStale();
              return;
            }
            if (err instanceof reviewService.QuotaExceededError) {
              closeToast();
              seriesRef.current = null;
              optsRef.current.onQuota(err);
              return;
            }
            if (!isRetryable(err)) {
              closeToast();
              seriesRef.current = null;
              toast.error(REVIEW_COPY.failHeadline);
              return;
            }
            if (attempt >= CONFIRM_RETRY_DELAYS_MS.length) {
              // Out of automatic retries: the toast says to tap Confirm all
              // and closes after 6 s; the bar re-enables. The series is
              // kept, so that tap sends the same batch with the same key.
              closeToast();
              toastIdRef.current = toast.error(REVIEW_COPY.failHeadline, {
                description: REVIEW_COPY.failBodyRetry,
                durationMs: CONFIRM_FAIL_TOAST_MS,
              });
              return;
            }
            if (!toastIdRef.current) {
              toastIdRef.current = toast.error(REVIEW_COPY.failHeadline, {
                description: REVIEW_COPY.failBody,
                persistent: true,
              });
            }
            await wait(CONFIRM_RETRY_DELAYS_MS[attempt]);
            if (!aliveRef.current) return;
          }
        }
      } finally {
        runningRef.current = false;
        if (aliveRef.current) setBusy(false);
      }
    },
    [closeToast],
  );

  const confirm = useCallback(async () => {
    if (runningRef.current) return;
    // The used-up toast (or the one-line refusal) leaves with the tap.
    closeToast();
    // A series kept after its retries ran out is sent again as it was (R9).
    const series = seriesRef.current ?? newSeries(optsRef.current);
    seriesRef.current = series;
    await runSeries(series);
  }, [closeToast, runSeries]);

  return { confirm, busy };
}
